<script>
    import { preventDefault } from 'svelte/legacy';
    import { cliConfigState } from '@app/store/cliConfigStore.svelte.js';
    import { toast } from '@zerodevx/svelte-toast';
    import { fade } from 'svelte/transition';
    import * as m from "@app/paraglide/messages.js";
    
    const ipcRenderer = window.ipcRenderer;
    let cliType = $state(0);
    
    function saveSettings() {
        localStorage.setItem("cc-cli-configs", JSON.stringify(cliConfigState));
        toast.push(m.options_cli_saved(), {classes: ['toast-success']});
    }

    function selectFileOption(cliOption) {
        ipcRenderer.invoke('selectFile', {
            title: m.options_cli_select_location({ tool: cliOption.name }),
            properties: ["showHiddenFiles", "treatPackageAsDirectory", "dontAddToRecent", "openFile"]
        }).then((path) => {
            if (!path.canceled) {
                cliOption.selected = path.filePaths[0];
            }
        });
    }
</script>

<div class="cli-container">
    <div class="cli-header">
        <h5>{m.options_cli_title()}</h5>
        <p class="text-muted mb-0">{m.options_cli_desc()}</p>
    </div>

    <div class="cli-card" transition:fade={{ duration: 150 }}>
        <div class="cli-card-header">
            <h6 class="fw-bold mb-0">{m.options_cli_developer_selection()}</h6>
        </div>
        <div class="cli-card-content">
            <form id="cliDeveloperSelectForm">
                <div class="mb-0">
                    <label class="form-label" for="cliTypeSelect">{m.options_cli_select_developer()}</label>
                    <select class="form-select" id="cliTypeSelect" bind:value={cliType}>
                        {#each cliConfigState as cliDeveloper, i}
                            <option value={i}>{cliDeveloper.name}</option>
                        {/each}
                    </select>
                </div>
            </form>
        </div>
    </div>

    <div class="cli-card" transition:fade={{ duration: 150 }}>
        <div class="cli-card-header">
            <h6 class="fw-bold mb-0">{m.options_cli_configuration_title({ name: cliConfigState[cliType]?.name || "" })}</h6>
        </div>
        <div class="cli-card-content">
            <form id="cliToolsForm" onsubmit={preventDefault(saveSettings)} aria-label={m.options_cli_form_label()}>
                {#each cliConfigState[cliType].options as cliOption, optionIndex}
                    {#if cliOption.type === "file-input"}
                        <div class="mb-3">
                            <label for="{cliOption.name}Input" class="form-label">{cliOption.name}</label>
                            <div class="input-group">
                                <input 
                                    type="text" 
                                    class="form-control" 
                                    readonly 
                                    value={cliOption.selected} 
                                    aria-label={m.options_cli_selected_path_label({ tool: cliOption.name })}
                                >
                                <button 
                                    id="chooseFileButton" 
                                    type="button" 
                                    class="btn btn-outline-secondary" 
                                    onclick={() => {selectFileOption(cliOption)}} 
                                    aria-label={m.options_cli_select_file_label()}
                                >
                                    {m.options_cli_browse()}
                                </button>
                            </div>
                            <small class="form-text text-muted">{m.options_cli_select_executable_help({ tool: cliOption.name })}</small>
                        </div>
                    {:else if cliOption.type === "list-input"}
                        <div class="mb-3">
                            <label class="form-label" for={`listOption${optionIndex}`}>{cliOption.name}</label>
                            <select class="form-select" id={`listOption${optionIndex}`} bind:value={cliOption.selected}>
                                {#each cliOption.values as optionValue}
                                    <option>{optionValue}</option>
                                {/each}
                            </select>
                        </div>
                    {/if}
                {/each}
                
                <div class="save-button-container">
                    <button 
                        id="saveCliSettingsButton" 
                        class="btn btn-primary" 
                        type="button" 
                        onclick={saveSettings} 
                        aria-label={m.options_cli_save_settings_label()}
                    >
                        {m.options_cli_save_settings()}
                    </button>
                </div>
            </form>
        </div>
    </div>

    <div class="cli-card" transition:fade={{ duration: 150 }}>
        <div class="cli-card-header">
            <h6 class="fw-bold mb-0">{m.options_cli_summary_title()}</h6>
        </div>
        <div class="cli-card-content p-0">
            <div class="table-responsive">
                <table class="table table-sm mb-0">
                    <thead class="table-light">
                        <tr>
                            <th scope="col">{m.options_cli_table_developer()}</th>
                            <th scope="col">{m.options_cli_table_option()}</th>
                            <th scope="col">{m.options_cli_table_value()}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each cliConfigState as cliDeveloper, i}
                            {#each cliDeveloper.options as cliOption, j}
                                <tr class={cliType === i ? "table-active" : ""}>
                                    <td>{cliDeveloper.name}</td>
                                    <td>{cliOption.name}</td>
                                    <td class="text-truncate" style="max-width: 250px;">{cliOption.selected}</td>
                                </tr>
                            {/each}
                        {/each}
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</div>

<style>
    .cli-container {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }

    .cli-header {
        margin-bottom: 0.5rem;
    }

    .cli-header h5 {
        margin-bottom: 0.25rem;
        font-weight: 600;
    }

    .cli-card {
        background-color: var(--bs-light);
        border-radius: 0.5rem;
        overflow: hidden;
        box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    }

    .cli-card-header {
        padding: 1rem;
        border-bottom: 1px solid rgba(0,0,0,0.1);
        display: flex;
        align-items: center;
    }

    .cli-card-content {
        padding: 1rem;
    }

    .save-button-container {
        display: flex;
        justify-content: flex-end;
        margin-top: 1rem;
    }

    /* Make the table more readable */
    :global(.cli-container .table) {
        margin-bottom: 0;
    }
    
    :global(.cli-container .table td) {
        padding: 0.5rem 1rem;
        vertical-align: middle;
    }
    
    :global(.cli-container .table-active) {
        font-weight: 500;
    }
</style>
